import { useTutorialActions, useTutorialState } from '@src/shared/context/tutorial';
import React, { ReactNode, useEffect, useState, useRef, CSSProperties } from 'react';
import styles from './TutorialManager.module.scss';
import { createPortal } from 'react-dom';
import { Tooltip } from 'snacks-design-system';
import useWindowResizeHandler from '@src/shared/hooks/use-window-resize-handler';
import useInViewport from '@src/shared/hooks/use-in-viewport';

export interface Step {
  targetElementSelector: string;
  transmitClick?: boolean;
  content: (object: any) => ReactNode;
  position?: string;
  tooltipSettings?: (object: any) => any;
  useTooltipStepSpecific?: Boolean;
  highlighterCustomCss?: CSSProperties;
  scrollToElement?: Boolean;
  beforeStepInitialized?: () => void;
  beforeStepLeave?: ({
    isMovingToPreviousPage,
    isMovingToNextPage,
  }: {
    isMovingToPreviousPage: Boolean;
    isMovingToNextPage: Boolean;
  }) => void;
  delayBeforeStepInitialize?: number | Promise<Boolean> | (() => Promise<Boolean>);
}

interface componentProps {
  steps: Array<Step>;
  tutorialID: string;
  onBeforeStart?: () => void;
  onAfterEnd?: () => void;
  onFinish?: () => void;
  onStepChange?: (currentStep: number) => void;
  useTooltipStepSpecific?: Boolean;
  predefinedStep?: number;
  useTooltip?: Boolean;
}

const baseHighlighterStyles: React.CSSProperties = {
  boxShadow: 'rgba(33, 33, 33, 0) 0px 0px 1px 2px, rgba(33, 33, 33, 0.5) 0px 0px 0px 5000px',
  zIndex: 10,
  position: 'absolute',
};
function observerHandler() {
  const { observerHandlerCallback } = window as any;
  if (observerHandlerCallback) {
    observerHandlerCallback();
  }
}
const bodyObserver: MutationObserver | null = new MutationObserver(() => observerHandler());
bodyObserver.observe(document.body, { childList: true, subtree: true });

const TutorialManager = (props: componentProps) => {
  const { activeTutorial } = useTutorialState();
  const { stopTutorial } = useTutorialActions();
  const [currentStep, setCurrentStep] = useState<null | number>(null);
  const [fetchedTargetElement, setFetchedTargetElement] = useState<null | HTMLElement>(null);
  const [highlighterStyles, setHighlighterStyles] =
    useState<React.CSSProperties>(baseHighlighterStyles);
  const [highlighterInnerContainerStyles, setHighlighterInnerContainerStyles] =
    useState<React.CSSProperties>({});
  const { isWindowResizing } = useWindowResizeHandler(1);
  const {
    steps = [] as Array<Step>,
    onBeforeStart = () => {},
    onAfterEnd = () => {},
    onFinish = () => {},
  } = props;
  const higlighterContainer = useRef(null);
  const isFetchedElementInViewPort = useInViewport(
    {
      current: fetchedTargetElement,
    },
    true
  );
  const tooltipRef = useRef(null);
  const isTooltipInViewPort = useInViewport(
    {
      current: tooltipRef?.current?.element as any,
    },
    true
  );

  const scrollToElement = (targetElement: HTMLElement) => {
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetHeight * 2 - targetElement.offsetTop,
      });
    }
  };

  const observeTargetElement = (selector: string) => {
    const targetElementObserver = new MutationObserver(() => {
      const fetchedElement = document.querySelector(selector) as HTMLElement;
      if (fetchedElement) {
        setFetchedTargetElement(fetchedElement);
        targetElementObserver.disconnect();
      }
    });

    targetElementObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return targetElementObserver;
  };

  const findTargetElement = () => {
    if (!currentStep) {
      return;
    }

    const { targetElementSelector } = steps[currentStep - 1];
    const fetchedElement = document.querySelector(targetElementSelector) as HTMLElement;
    if (!fetchedElement) {
      const observer = observeTargetElement(targetElementSelector);
      return () => observer?.disconnect();
    } else {
      setFetchedTargetElement(fetchedElement);
    }
  };

  const calculateStyles = () => {
    const { x, y, width, height } = fetchedTargetElement?.getBoundingClientRect() as any;
    const computedStyles = getComputedStyle(fetchedTargetElement as any);
    const { highlighterCustomCss = {} } = currentStepConfig();
    const updatedStyles = {
      ...baseHighlighterStyles,
      top: `${y + window.scrollY}px`,
      left: `${x + window.scrollX}px`,
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: computedStyles.getPropertyValue('border-radius'),
      ...highlighterCustomCss,
    };
    const innerContainerStyles = {
      width: updatedStyles.width,
      height: updatedStyles.height,
      borderRadius: updatedStyles.borderRadius,
    };

    return { updatedStyles, innerContainerStyles };
  };

  const applyHighlighter = () => {
    const { updatedStyles, innerContainerStyles } = calculateStyles();
    document.body.style.overflow = 'hidden';
    window.observerHandler = applyHighlighter;
    setHighlighterStyles(updatedStyles);
    setHighlighterInnerContainerStyles(innerContainerStyles);
    setPositionOfStepContent();
  };

  useEffect(() => {
    if (fetchedTargetElement) {
      applyHighlighter();
    }
  }, [
    fetchedTargetElement?.getBoundingClientRect().x,
    fetchedTargetElement?.getBoundingClientRect().y,
    fetchedTargetElement?.getBoundingClientRect().height,
    fetchedTargetElement?.getBoundingClientRect().width,
  ]);

  const clearPredefinedPositions = (element: HTMLElement) => {
    element.style.left = 'unset';
    element.style.right = 'unset';
    element.style.top = 'unset';
    element.style.bottom = 'unset';
  };

  const setPositionOfStepContent = () => {
    const element = document.getElementById('step_content');
    const DEFAULT_MARGIN = 16;
    if (!element) {
      return;
    }

    const { width, height, left } = element.getBoundingClientRect();

    const { position } = currentStepConfig();

    element.style.visibility = 'hidden';

    clearPredefinedPositions(element);
    if (position === 'left') {
      element.style.left = `-${width + DEFAULT_MARGIN}px`;
    }
    if (position === 'right') {
      element.style.right = `-${width + DEFAULT_MARGIN}px`;
      element.style.top = `0px`;
    }

    if (position === 'top') {
      element.style.top = `-${height + DEFAULT_MARGIN}px`;
      element.style.left = '0';
    }

    if (position === 'bottom') {
      element.style.bottom = `-${height + DEFAULT_MARGIN}px`;
      element.style.left = '0';
    }
    setTimeout(() => {
      element.style.visibility = 'visible';
    }, 50);
  };

  useEffect(() => {
    if (!fetchedTargetElement) {
      return;
    }
    const { updatedStyles, innerContainerStyles } = calculateStyles();
    setHighlighterStyles(updatedStyles);
    setHighlighterInnerContainerStyles(innerContainerStyles);
  }, [isWindowResizing]);

  const nextStep = () => {
    if (currentStep && currentStep < steps.length) {
      const newStep = currentStep + 1;
      const newStepConfig = currentStepConfig(newStep);
      const currentStepConfiguration = currentStepConfig();

      if (currentStepConfiguration && currentStepConfiguration.beforeStepLeave) {
        currentStepConfiguration?.beforeStepLeave({
          isMovingToNextPage: true,
          isMovingToPreviousPage: false,
        });
      }

      executeStep({ step: newStep, stepConfig: newStepConfig });
      props.onStepChange && props.onStepChange(newStep);
    } else if (currentStep === steps.length) {
      onFinish();
    }
  };

  const previousStep = () => {
    if (currentStep && currentStep > 1) {
      const newStep = currentStep - 1;
      const newStepConfig = currentStepConfig(newStep);
      const currentStepConfiguration = currentStepConfig();

      if (currentStepConfiguration && currentStepConfiguration.beforeStepLeave) {
        currentStepConfiguration?.beforeStepLeave({
          isMovingToNextPage: false,
          isMovingToPreviousPage: true,
        });
      }
      executeStep({ step: newStep, stepConfig: newStepConfig });
      props.onStepChange && props.onStepChange(newStep);
    }
  };

  const goStep = (stepNumber: number) => {
    const newStep = stepNumber;
    executeStep({ step: newStep, stepConfig: currentStepConfig(newStep) });
    props.onStepChange && props.onStepChange(newStep);
  };

  const closeTutorial = () => {
    setCurrentStep(0);
    setFetchedTargetElement(null);
    onAfterEnd();
    document.body.style.overflow = '';
    stopTutorial();
  };

  useEffect(() => {
    findTargetElement();
  }, [currentStep]);

  useEffect(() => {
    if (fetchedTargetElement && isFetchedElementInViewPort !== null) {
      if (!isFetchedElementInViewPort && currentStepConfig().scrollToElement) {
        scrollToElement(fetchedTargetElement);
      }
      applyHighlighter();
    }
  }, [fetchedTargetElement, isFetchedElementInViewPort]);

  useEffect(() => {
    if (
      tooltipRef.current &&
      isTooltipInViewPort === false &&
      currentStepConfig().scrollToElement
    ) {
      scrollToElement(tooltipRef.current.element);
    }
  }, [tooltipRef.current, isTooltipInViewPort]);

  useEffect(() => {
    if (activeTutorial === props.tutorialID) {
      onBeforeStart();
      const step = props.predefinedStep || 1;
      const stepConfig = currentStepConfig(step);
      props.onStepChange && props.onStepChange(step);
      executeStep({ step, stepConfig });
    }
  }, [activeTutorial]);

  const executeStep = async ({ step, stepConfig }: { step: any; stepConfig: any }) => {
    setCurrentStep(null);
    if (!stepConfig.delayBeforeStepInitialize) {
      if (stepConfig.beforeStepInitialized) {
        stepConfig.beforeStepInitialized();
      }
      setCurrentStep(step);
    } else {
      if (stepConfig.beforeStepInitialized) {
        stepConfig.beforeStepInitialized();
      }
      if (typeof stepConfig.delayBeforeStepInitialize === 'number') {
        setTimeout(() => {
          setCurrentStep(step);
        }, stepConfig.delayBeforeStepInitialize);
      } else if (stepConfig.delayBeforeStepInitialize instanceof Promise) {
        await stepConfig.delayBeforeStepInitialize;
        setCurrentStep(step);
      } else if (typeof stepConfig.delayBeforeStepInitialize === 'function') {
        const promise = stepConfig.delayBeforeStepInitialize();
        await promise;
        setCurrentStep(step);
      }
    }
  };

  useEffect(() => {}, [isWindowResizing]);

  const transmitClickToTheSourceElement = () => {
    if (fetchedTargetElement) {
      fetchedTargetElement.click();
    }
  };

  const currentStepConfig = (predefinedStep: number = 0) => {
    const step = predefinedStep || currentStep;
    return steps[(step || 1) - 1];
  };

  return currentStep && fetchedTargetElement
    ? createPortal(
        <>
          <div
            ref={higlighterContainer}
            className={styles.tutorialHighlighter}
            id="tutorialHighlighter"
            style={highlighterStyles}
            onClick={(e) => {
              if (currentStepConfig().transmitClick) {
                transmitClickToTheSourceElement();
              } else {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            {props.useTooltip && currentStepConfig().useTooltipStepSpecific !== false && (
              <Tooltip
                {...currentStepConfig().tooltipSettings({
                  nextStep,
                  previousStep,
                  closeTutorial,
                  goStep,
                })}
                ref={tooltipRef}
                isOpen={true}
                toggle={
                  <div
                    id="tutorialHighlighterInnerContainer"
                    onClick={(e) => {
                      if (currentStepConfig().transmitClick) {
                        transmitClickToTheSourceElement();
                      } else {
                        e.stopPropagation();
                        e.preventDefault();
                      }
                    }}
                    style={highlighterInnerContainerStyles}
                  ></div>
                }
              >
                {currentStepConfig().content({
                  nextStep,
                  previousStep,
                  closeTutorial,
                  goStep,
                })}
              </Tooltip>
            )}
            {(!props.useTooltip || currentStepConfig().useTooltipStepSpecific === false) && (
              <div id="step_content" className={styles.innerCard}>
                {currentStepConfig().content({
                  nextStep,
                  previousStep,
                  closeTutorial,
                  goStep,
                })}
              </div>
            )}
          </div>
          <div
            className={styles.highlighterOverlay}
            id={'higlighterOverlay'}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          ></div>
        </>,
        document.body
      )
    : null;
};

export default TutorialManager;
